<script setup lang="ts">
import { __ } from '@@/bits/intl'
import { DEFAULT_MAX_GRADE } from '@@/bits/surface_settings_helper'
import { defineAsyncComponent } from '@@/bits/vue'
import { useDarkModeStore } from '@@/pinia/dark_mode'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useReactionsStore } from '@@/pinia/surface_reactions'
import { useSurfaceSettingsStore } from '@@/pinia/surface_settings'
import type {
  AccumulatedEmojiReactionData,
  AccumulatedNonEmojiReactionData,
  Post,
  UserId,
  WallReactionData,
} from '@@/types'
import type { WallColorScheme } from '@padlet/arvo/src/types'
import { storeToRefs } from 'pinia'
import type { ComponentPublicInstance } from 'vue'
import { computed, ref } from 'vue'

const SurfacePostReactionLike = defineAsyncComponent(() => import('@@/vuecomponents/SurfacePostReactionLike.vue'))
const SurfacePostReactionVote = defineAsyncComponent(() => import('@@/vuecomponents/SurfacePostReactionVote.vue'))
const SurfacePostReactionGrade = defineAsyncComponent(() => import('@@/vuecomponents/SurfacePostReactionGrade.vue'))
const SurfacePostReactionStar = defineAsyncComponent(() => import('@@/vuecomponents/SurfacePostReactionStar.vue'))
const SurfacePostReactionEmoji = defineAsyncComponent(() => import('@@/vuecomponents/SurfacePostReactionEmoji.vue'))

const props = defineProps<{
  post: Post
  isReacting: boolean
  currentUserId: UserId
  reactionData: WallReactionData
  colorScheme: WallColorScheme
  usageContext: 'postView' | 'postExpandedView'
}>()

const { isFrozen } = storeToRefs(useSurfaceStore())
const { createReaction, deleteReaction, updateReaction, startEditingReaction } = useReactionsStore()
const { accumulatedReactionsByWishId, currentReactionData } = storeToRefs(useReactionsStore())
const reactionType = computed(() => currentReactionData.value.type)
const accumulatedReaction = computed(() => {
  if (
    accumulatedReactionsByWishId.value[props.post.id as number] == null ||
    accumulatedReactionsByWishId.value[props.post.id as number]?.reactionType !== reactionType.value
  ) {
    return reactionType.value === 'emoji'
      ? ({
          emojis: {},
          totalReactionsCount: 0,
          reactionType: reactionType.value,
        } as AccumulatedEmojiReactionData)
      : ({
          sum: 0,
          totalReactionsCount: 0,
          reactionType: reactionType.value,
        } as AccumulatedNonEmojiReactionData)
  }
  return accumulatedReactionsByWishId.value[props.post.id as number]
})
const maxGrade = computed(() => currentReactionData.value?.max_value ?? DEFAULT_MAX_GRADE)
const { setSnackbar } = useGlobalSnackbarStore()
const rootElement = ref<ComponentPublicInstance | null>(null)
function create(value: number | string): void {
  if (rootElement.value == null) return
  if (rootElement.value.$el.closest('.surface-post')?.classList.contains('noclick')) {
    // Prevent reaction event when the post is being dragged
    return
  }
  createReaction({ postId: props.post.id as number, value })
}

function update(value: number): void {
  if (rootElement.value == null) return
  if (rootElement.value.$el.closest('.surface-post')?.classList.contains('noclick')) {
    // Prevent reaction event when the post is being dragged
    return
  }
  updateReaction({ postId: props.post.id as number, value })
}

function edit(): void {
  if (rootElement.value == null) return
  if (rootElement.value.$el.closest('.surface-post')?.classList.contains('noclick')) {
    // Prevent reaction event when the post is being dragged
    return
  }
  const { left, top, width } = (rootElement.value?.$el?.parentElement ?? rootElement.value?.$el).getBoundingClientRect()

  startEditingReaction({
    postId: props.post.id as number,
    dropdownLeft: left,
    dropdownTop: top,
    dropdownWidth: width,
  })
}

function deleteExisting(): void {
  if (rootElement.value == null) return
  if (rootElement.value.$el.closest('.surface-post')?.classList.contains('noclick')) {
    // Prevent reaction event when the post is being dragged
    return
  }
  deleteReaction({ postId: props.post.id as number })
}

function showReactionOffNotice(): void {
  if (isFrozen.value) {
    setSnackbar({
      message: __('Reactions are disabled on frozen padlets.'),
      notificationType: SnackbarNotificationType.error,
    })
  } else if (!isReactable.value) {
    setSnackbar({
      message: __('Reactions are disabled. Changes are not allowed.'),
      notificationType: SnackbarNotificationType.error,
    })
  } else {
    // if !canIReact.value
    setSnackbar({
      message: __('You do not have permission to react on this padlet.'),
      notificationType: SnackbarNotificationType.error,
    })
  }
}

const isPadletColorSchemeDark = computed(() => props.colorScheme === 'dark')
const darkModeStore = useDarkModeStore()
const componentDarkMode = computed(() =>
  props.usageContext === 'postExpandedView' ? darkModeStore.isDarkMode : isPadletColorSchemeDark.value,
)
const { canIReact } = storeToRefs(useSurfacePermissionsStore())
const { isReactable } = storeToRefs(useSurfaceSettingsStore())
const canReact = computed(() => canIReact.value && isReactable.value)
</script>

<template>
  <SurfacePostReactionLike
    v-if="reactionType === 'like'"
    ref="rootElement"
    key="reaction-like"
    data-testid="surfacePostReactionLike"
    :count="accumulatedReaction.totalReactionsCount"
    :user-reaction="accumulatedReaction.userReaction"
    :is-padlet-color-scheme-dark="isPadletColorSchemeDark"
    :can-react="canReact"
    :usage-context="usageContext"
    :post-cid="post.cid"
    @create="create"
    @delete="deleteExisting"
    @edit-when-reaction-is-off="showReactionOffNotice"
  />
  <SurfacePostReactionVote
    v-else-if="reactionType === 'vote'"
    ref="rootElement"
    key="reaction-vote"
    data-testid="surfacePostReactionVote"
    :sum="accumulatedReaction.sum"
    :count="accumulatedReaction.totalReactionsCount"
    :user-reaction="accumulatedReaction.userReaction"
    :is-padlet-color-scheme-dark="isPadletColorSchemeDark"
    :can-react="canReact"
    :usage-context="usageContext"
    :post-cid="post.cid"
    @create="create"
    @update="update"
    @delete="deleteExisting"
    @edit-when-reaction-is-off="showReactionOffNotice"
  />
  <SurfacePostReactionGrade
    v-else-if="reactionType === 'grade'"
    ref="rootElement"
    key="reaction-grade"
    data-testid="surfacePostReactionGrade"
    :sum="accumulatedReaction.sum"
    :count="accumulatedReaction.totalReactionsCount"
    :user-reaction="accumulatedReaction.userReaction"
    :max-grade="maxGrade"
    :is-padlet-color-scheme-dark="isPadletColorSchemeDark"
    :can-react="canReact"
    :usage-context="usageContext"
    :post-cid="post.cid"
    @edit="edit"
    @edit-when-reaction-is-off="showReactionOffNotice"
  />
  <SurfacePostReactionStar
    v-else-if="reactionType === 'star'"
    ref="rootElement"
    key="reaction-star"
    data-testid="surfacePostReactionStar"
    :sum="accumulatedReaction.sum"
    :count="accumulatedReaction.totalReactionsCount"
    :user-reaction="accumulatedReaction.userReaction"
    :max-grade="maxGrade"
    :is-padlet-color-scheme-dark="isPadletColorSchemeDark"
    :can-react="canReact"
    :usage-context="usageContext"
    :post-cid="post.cid"
    @edit="edit"
    @edit-when-reaction-is-off="showReactionOffNotice"
  />
  <SurfacePostReactionEmoji
    v-else-if="reactionType === 'emoji'"
    ref="rootElement"
    key="reaction-emoji"
    data-testid="surfacePostReactionEmoji"
    :accumulated-reactions="accumulatedReaction"
    :is-dark-mode="componentDarkMode"
    :can-react="canReact"
    :usage-context="usageContext"
    :post-color="usageContext === 'postExpandedView' ? null : post.color"
    :post-cid="post.cid"
    :post-id="post.id"
    @create="create"
    @update="update"
    @delete="deleteExisting"
    @edit-when-reaction-is-off="showReactionOffNotice"
  />
  <div v-else></div>
</template>
